import {useState, useEffect} from "react"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    TextField,
    Button,
    MenuItem,
    CircularProgress,
} from "@mui/material"
import {useTranslation} from "react-i18next"
import axiosInstance from "../config/axios-config"
import i18n from "../i18n/i18n"
import {useUser} from "../providers/user-context";

// Roles an admin is allowed to create
const ASSIGNABLE_ROLES = ["OPERATOR", "COURIER"]

const AddUser = ({open, onClose, onUserCreated}) => {
    const {t} = useTranslation()
    const {tenantId} = useUser();

    const [form, setForm] = useState({
        name: "",
        surname: "",
        email: "",
        role: "OPERATOR",
    })
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState("")

    useEffect(() => {
        if (open) {
            setForm({name: "", surname: "", email: "", password: "", role: "OPERATOR"})
            setError("")
        }
    }, [open])

    const handleChange = (field) => (e) => {
        setForm((prev) => ({...prev, [field]: e.target.value}))
    }

    const isValid =
        form.name.trim() &&
        form.surname.trim() &&
        form.email.trim() &&
        ASSIGNABLE_ROLES.includes(form.role)

    const handleSubmit = async () => {
        if (!isValid) return
        setSaving(true)
        setError("")
        try {
            const response = await axiosInstance.post(
                "/api/v1/users",
                {
                    name: form.name,
                    surname: form.surname,
                    email: form.email,
                    role: form.role,
                    tenantId: tenantId,
                    active: true,
                },
                {
                    headers: {
                        "Language-Context": i18n.language.toUpperCase() || "EN",
                    },
                },
            )
            if (onUserCreated) {
                onUserCreated(response.data?.data || response.data)
            }
            onClose()
        } catch (err) {
            console.error("Error creating user:", err)
            setError(err.response?.data?.message || t("FailedToCreateUser") || "Failed to create user")
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t("AddUser") || "Add User"}</DialogTitle>
            <DialogContent dividers>
                <Box component="form" sx={{display: "flex", flexDirection: "column", gap: 2, mt: 1}}>
                    <TextField
                        label={t("FirstName")}
                        fullWidth
                        value={form.name}
                        onChange={handleChange("name")}
                    />
                    <TextField
                        label={t("LastName")}
                        fullWidth
                        value={form.surname}
                        onChange={handleChange("surname")}
                    />
                    <TextField
                        label={t("Email")}
                        type="email"
                        fullWidth
                        value={form.email}
                        onChange={handleChange("email")}
                    />
                    <TextField
                        select
                        label={t("Role") || "Role"}
                        fullWidth
                        value={form.role}
                        onChange={handleChange("role")}
                    >
                        {ASSIGNABLE_ROLES.map((role) => (
                            <MenuItem key={role} value={role}>
                                {t(role) || role}
                            </MenuItem>
                        ))}
                    </TextField>

                    {error && (
                        <Box sx={{color: "error.main", fontSize: "0.875rem"}}>{error}</Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    {t("Cancel")}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!isValid || saving}
                    startIcon={saving ? <CircularProgress size={18} color="inherit"/> : null}
                >
                    {saving ? t("Saving") : t("AddUser") || "Add User"}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default AddUser
